import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Info } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Container, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { ContinueToPaymentButton } from './continue-button';
import { AccordionContent, AccordionItem, AccordionTrigger } from './faq-accordion';
import { FeaturesContainer } from './features-container';
import { PlansContainer } from './plans-container';
import { StyledPaper } from './styled-components';
import { resolveBranding } from './utils';
export const MembershipPlansPreview = ({ config, title = 'Choose Your Plan', subtitle = 'Select the membership tier that best fits your needs', showSetupCheckoutHint = false, showFaq = true, showContactSupport = false, supportImageUrl, sx, selectedPlanId: controlledSelectedPlanId, billingPeriod: controlledBillingPeriod, onSelectedPlanChange, onBillingPeriodChange, onContinueToPayment, isContinueLoading = false, isContinueDisabled = false, continueButtonLabel = 'Continue to Payment', branding: brandingProp, }) => {
    const [internalSelectedPlanId, setInternalSelectedPlanId] = useState();
    const [internalBillingPeriod, setInternalBillingPeriod] = useState('monthly');
    const [expandedFaq, setExpandedFaq] = useState(false);
    const branding = useMemo(() => resolveBranding(brandingProp), [brandingProp]);
    const primaryColor = alpha(branding.primaryColor, 1);
    const isControlled = controlledSelectedPlanId !== undefined && onSelectedPlanChange !== undefined;
    const selectedPlanId = isControlled ? controlledSelectedPlanId : internalSelectedPlanId;
    const setSelectedPlanId = isControlled
        ? (id) => onSelectedPlanChange?.(id)
        : setInternalSelectedPlanId;
    const isBillingControlled = controlledBillingPeriod !== undefined && onBillingPeriodChange !== undefined;
    const billingPeriod = isBillingControlled ? controlledBillingPeriod : internalBillingPeriod;
    const setBillingPeriod = isBillingControlled
        ? (p) => onBillingPeriodChange?.(p)
        : setInternalBillingPeriod;
    const options = config?.options;
    const products = useMemo(() => config?.products ?? [], [config?.products]);
    const features = useMemo(() => config?.features ?? [], [config?.features]);
    const effectiveBillingPeriod = useMemo(() => {
        if (options?.showMonthlyPrices && !options?.showAnnualPrices)
            return 'monthly';
        if (options?.showAnnualPrices && !options?.showMonthlyPrices)
            return 'annual';
        return billingPeriod;
    }, [options?.showMonthlyPrices, options?.showAnnualPrices, billingPeriod]);
    useEffect(() => {
        if (!isControlled && products.length === 0) {
            setInternalSelectedPlanId(undefined);
            return;
        }
        if (!isControlled &&
            options?.mostPopularProductId &&
            products.some((p) => p.id === options.mostPopularProductId)) {
            setInternalSelectedPlanId(options.mostPopularProductId);
        }
        else if (!isControlled && products.length > 0) {
            setInternalSelectedPlanId((prev) => prev && products.some((p) => p.id === prev) ? prev : products[0].id);
        }
    }, [isControlled, options?.mostPopularProductId, products]);
    const selectedPlan = useMemo(() => products.find((p) => p.id === selectedPlanId), [products, selectedPlanId]);
    const faqQuestions = options?.faqQuestions ?? [];
    const shouldShowFaq = showFaq && Boolean(options?.showFaq) && faqQuestions.length > 0;
    if (!config) {
        return null;
    }
    const content = (_jsxs(Box, { sx: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            ...sx,
        }, children: [_jsxs(StyledPaper, { sx: { maxWidth: '1200px', backgroundColor: 'white', mx: 'auto' }, children: [_jsxs(Box, { sx: { textAlign: 'center', mb: { xs: 4, md: 5 } }, children: [_jsx(Typography, { variant: "h3", component: "h1", sx: { mb: 1 }, color: "black", children: title }), _jsx(Typography, { variant: "body1", color: "#6b7280", children: subtitle }), showSetupCheckoutHint && options?.setupCheckoutText && options?.isSetupMode ? (_jsxs(Box, { sx: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    mt: 2,
                                    px: 2,
                                    py: 1,
                                    borderRadius: '4px',
                                    bgcolor: 'rgba(42, 197, 201, 0.08)',
                                }, children: [_jsx(Info, { sx: { fontSize: 14, color: 'rgba(42, 197, 201, 1)', opacity: 0.8 } }), _jsx(Typography, { variant: "body2", sx: {
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: '0.875rem',
                                            color: '#111827',
                                        }, children: options.setupCheckoutText })] })) : null] }), _jsxs(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                            gap: { xs: 3, md: 4 },
                            mb: { xs: 4, md: 5 },
                        }, children: [_jsx(Box, { sx: { display: { xs: 'none', lg: 'block' } }, children: _jsx(FeaturesContainer, { features: features, selectedPlan: selectedPlan, branding: branding }) }), _jsx(PlansContainer, { plans: products, branding: branding, callForPricingPhone: options?.callForPricingPhone ?? '', showMonthlyPrices: options?.showMonthlyPrices ?? false, showAnnualPrices: options?.showAnnualPrices ?? false, mostPopularPlanId: options?.mostPopularProductId ?? null, selectedPlanId: selectedPlanId, setSelectedPlanId: setSelectedPlanId, billingPeriod: effectiveBillingPeriod, setBillingPeriod: setBillingPeriod, renderFeatures: (planId) => (_jsx(FeaturesContainer, { features: features, selectedPlan: products.find((p) => p.id === planId), branding: branding })) })] }), _jsxs(Box, { sx: {
                            pt: { xs: 3, md: 4 },
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            alignItems: { xs: 'stretch', lg: 'center' },
                            justifyContent: 'space-between',
                            gap: 3,
                        }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }, children: showContactSupport &&
                                    options?.showContactSupport &&
                                    options?.contactSupportLink ? (_jsxs(_Fragment, { children: [supportImageUrl ? (_jsx(Box, { component: "img", src: supportImageUrl, alt: "Support", sx: {
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                flexShrink: 0,
                                            } })) : null, _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { color: '#111827', mb: 0.5 }, children: "Have questions?" }), _jsx(Typography, { variant: "body2", sx: { color: '#6b7280', mb: 1 }, children: "Schedule a 15-minute call with our team." }), _jsx(Typography, { component: "a", href: options.contactSupportLink, target: "_blank", rel: "noopener noreferrer", variant: "body2", sx: {
                                                        color: primaryColor,
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }, children: "Chat with a Membership Expert" })] })] })) : null }), _jsx(Box, { sx: {
                                    display: { xs: 'none', lg: 'flex' },
                                    flexShrink: 0,
                                    alignItems: 'center',
                                }, children: _jsx(ContinueToPaymentButton, { onClick: onContinueToPayment ??
                                        (() => {
                                            /* no-op when uncontrolled */
                                        }), branding: branding, loading: isContinueLoading, disabled: onContinueToPayment !== undefined && onContinueToPayment !== null
                                        ? isContinueDisabled
                                        : false, label: continueButtonLabel }) })] })] }), shouldShowFaq ? (_jsx(Box, { sx: {
                    mt: 4,
                    bgcolor: '#f9fafb',
                    width: '100%',
                    maxWidth: '100%',
                    mx: { xs: -2, md: -4 },
                    px: { xs: 2, md: 4 },
                    py: 6,
                    borderTop: '1px solid #e5e7eb',
                    boxSizing: 'border-box',
                }, children: _jsxs(Container, { maxWidth: "lg", children: [_jsx(Typography, { variant: "h4", component: "h2", sx: { textAlign: 'center', mb: 4, color: 'black' }, children: "FAQs" }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: faqQuestions.map((faq) => (_jsxs(AccordionItem, { expanded: expandedFaq === `${faq.question}-${faq.answer}`, onChange: (_, isExpanded) => setExpandedFaq(isExpanded ? `${faq.question}-${faq.answer}` : false), sx: {
                                    backgroundColor: 'white',
                                    border: 0,
                                    borderRadius: 1,
                                    px: 2,
                                    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                                    transition: 'all 0.2s ease',
                                }, children: [_jsx(AccordionTrigger, { primaryColor: primaryColor, expandIcon: _jsx(ExpandMoreIcon, { sx: { transition: 'transform 0.2s', color: '#111827' } }), sx: {
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            color: '#111827',
                                            py: 1.5,
                                            transition: 'color 0.2s ease',
                                            '&:hover': { textDecoration: 'none', color: primaryColor },
                                            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                                transform: 'rotate(180deg)',
                                            },
                                        }, children: faq.question }), _jsx(AccordionContent, { sx: {
                                            fontSize: '0.875rem',
                                            color: 'black',
                                            px: 0,
                                            pb: 1.5,
                                            whiteSpace: 'pre-line',
                                        }, children: faq.answer })] }, `${faq.question}-${faq.answer}`))) })] }) })) : null] }));
    return content;
};
